import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useThemeStore } from '../store/themeStore';

interface AuthLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

const AuthLayout: React.FC<AuthLayoutProps> = ({ children, title, subtitle }) => {
  const { theme } = useThemeStore();

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 ${
        theme === 'dark' ? 'bg-gpt-dark-main' : 'bg-gpt-light-main'
      }`}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="w-full max-w-[400px]"
      >
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, type: 'spring' }}
          className="flex justify-center mb-8"
        >
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center ${
              theme === 'dark' ? 'bg-white' : 'bg-black'
            }`}
          >
            <Sparkles size={24} className={theme === 'dark' ? 'text-black' : 'text-white'} />
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-8"
        >
          <h1
            className={`text-3xl font-bold mb-2 ${
              theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
            }`}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              className={`text-sm ${
                theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'
              }`}
            >
              {subtitle}
            </p>
          )}
        </motion.div>

        {/* Form Content */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {children}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AuthLayout;